import type { ComponentType, HTMLAttributes, ReactNode, SVGProps } from 'react';
import { cn } from '@/lib/utils';
import { Icon as SvgIcon } from '../icon';
import { CloseIcon } from '@/shared-ui/icons/close';
import { ErrorIcon } from '@/shared-ui/icons/error';
import { SuccessIcon } from '@/shared-ui/icons/success';
import { WarningIcon } from '@/shared-ui/icons/warning';

export type ToastVariant = 'success' | 'error' | 'warning';

const toastIconMap: Record<ToastVariant, ComponentType<SVGProps<SVGSVGElement>>> = {
    success: SuccessIcon,
    error: ErrorIcon,
    warning: WarningIcon,
};

interface ToastProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
    description: ReactNode;
    onClose: () => void;
    title: ReactNode;
    variant: ToastVariant;
}

export const Toast = ({ className, description, onClose, title, variant, ...props }: ToastProps) => {
    const VariantIcon = toastIconMap[variant];

    return (
        <div
            data-slot="toast"
            data-variant={variant}
            className={cn(
                'relative flex w-full items-center justify-between rounded-[4px] bg-background shadow-toast border border-border py-4 pl-[21px] pr-4 text-primary gap-4',
                {
                    'border-success': variant === 'success',
                    'border-destructive': variant === 'error',
                    'border-warning': variant === 'warning',
                },
                className
            )}
            {...props}>
            <div
                aria-hidden="true"
                data-slot="toast-accent"
                className={cn('absolute left-0 top-0 w-[5px] h-full', {
                    'bg-success': variant === 'success',
                    'bg-destructive': variant === 'error',
                    'bg-warning': variant === 'warning',
                })}
            />

            <SvgIcon className="size-6" icon={VariantIcon} />

            <div data-slot="toast-content" className="min-w-0 flex-1 space-y-1">
                <p data-slot="toast-title" className="text-sm font-normal">
                    {title}
                </p>

                <div data-slot="toast-description" className="text-xs font-light tracking-[0.025rem]">
                    {description}
                </div>
            </div>

            <button aria-label="Close" data-slot="toast-close-button" onClick={onClose} type="button">
                <SvgIcon className="size-4 text-primary" icon={CloseIcon} />
            </button>
        </div>
    );
};
